import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import FacebookVerifiedBadge from '@src/svgs/FacebookVerifiedBadge';
import { FullScreenIconAlt } from '@src/svgs/FullScreenIcon';
import GearIcon from '@src/svgs/GearIcon';
import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import SpeakerIcon from '@src/svgs/SpeakerIcon';
import SpeakerMutedIcon from '@src/svgs/SpeakerMutedIcon';
import YouTubeCCIcon from '@src/svgs/YouTubeCCIcon';
import YouTubeMiniplayerIcon from '@src/svgs/YouTubeMiniplayerIcon';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { BrandLogo, BrandName } from '../BrandSlots';
import { formatPlaybackTime } from '../formatPlaybackTime';
import { useChromeFontSize } from '../useChromeFontSize';
import { OverlayFrame } from './sharedChrome';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.facebookFeedPost;

/**
 * Facebook Feed Post overlay — paints the chrome that sits *on top of* a
 * Facebook feed video: the top author header (avatar + display name +
 * verified + ellipsis) along the top edge, and the in-video player chrome
 * (play / time / progress / settings / captions / picture-in-picture /
 * fullscreen / mute) along the bottom edge. Captions anchor in the
 * lower-middle area, above the player chrome.
 *
 * The reactions summary + Like / Comment / Share action row that surround a
 * feed post in the real Facebook UI are intentionally *not* painted — those
 * live below the video on the actual feed, not over the video, so they're
 * not a safe-zones concern.
 *
 * Scaling: chrome is sized in `em` against a base font-size derived from the
 * rect. Reference rect 510×287 (16:9 at 510px wide) → 1em = 10px. At 1:1 the
 * width ratio wins so chrome paints at the same em-base as 16:9 — the
 * central video area between the top header and bottom chrome is taller,
 * which mirrors how Facebook actually renders a square video.
 */
const REFERENCE_RECT_WIDTH = 510;
const REFERENCE_RECT_HEIGHT = 287;
const REFERENCE_FONT_PX = 10;
const SCALE_MIN = 0.5;
const SCALE_MAX = 3;

// Facebook brand blue — used by the in-video progress bar fill so the chrome
// reads as Facebook-flavored rather than YouTube-red.
const FACEBOOK_BLUE = '#1877F2';

export const FacebookFeedPostOverlay: React.FC<SafeZoneProps> = ({
  rect,
  aspectRatio,
  variants,
  onToggleVariant,
  playback,
}) => {
  // 16:9 paints only the bottom player chrome — Facebook's autoplay feed at
  // landscape doesn't burn the author header into the video itself; the
  // header sits *above* the video in the surrounding feed UI. 1:1 (square)
  // is tighter in the feed and overlays the author chrome on top of the
  // video, so we keep painting it there.
  const showTopHeader = aspectRatio !== '16:9';
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_RECT_WIDTH,
    referenceHeight: REFERENCE_RECT_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_MIN,
    scaleMax: SCALE_MAX,
  });

  const showControls = !!variants.showPlayControls;
  const showCaptions = !!variants.showCaptions;

  // Live playback mirroring. When the bridge is absent (e.g. previewing in a
  // surface without a connected renderer), fall back to inert defaults — the
  // chrome still renders, it just doesn't reflect or drive a video.
  const isPlaying = playback?.isPlaying ?? false;
  const duration = playback?.duration ?? 0;
  const isReady = playback?.isReady ?? false;

  const {
    scrubberRef,
    displayTime,
    progressPct,
    scrubberInteractive,
    onMouseDown: onScrubberMouseDown,
  } = useScrubberDrag(playback);

  const togglePlayPause = () => {
    if (!playback || !isReady) return;
    if (isPlaying) playback.pause();
    else playback.play();
  };

  // Local mute visual state — the renderer doesn't expose a volume API yet,
  // so the speaker icon swap is for safe-zone accuracy rather than driving
  // playback. Defaults to `muted` because feed video autoplay is sound-off.
  const [isMuted, setIsMuted] = useState(true);

  // Chrome stays `pointer-events: none` so clicks pass through to the
  // renderer. CSS `:hover` can't fire on the play button — drive the
  // dim→bright transition ourselves by checking the cursor against the
  // button's bounding rect on every mousemove. Same pattern as the LinkedIn
  // / YouTube overlays.
  const playBtnRef = useRef<HTMLButtonElement | null>(null);
  const [playHovered, setPlayHovered] = useState(false);
  useEffect(() => {
    if (!showControls) return;
    const onMove = (e: MouseEvent) => {
      const el = playBtnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inside =
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom;
      setPlayHovered(prev => (prev === inside ? prev : inside));
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [showControls]);

  return (
    <OverlayFrame
      data-testid="facebook-feed-post-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      {showControls && showTopHeader && (
        <TopAuthorHeader>
          <Avatar>
            <BrandLogo />
          </Avatar>
          <BrandTextStack>
            <NameRow>
              <NameLine>
                <BrandName />
              </NameLine>
              <VerifiedSlot aria-label={COPY.aria.verified}>
                <FacebookVerifiedBadge width="1.2em" height="1.2em" />
              </VerifiedSlot>
            </NameRow>
            <Subtitle>{COPY.subtitleFallback}</Subtitle>
          </BrandTextStack>
          <MoreButton type="button" aria-label={COPY.aria.more}>
            <EllipsisIcon width="1.6em" height="0.4em" fillColor="#ffffff" />
          </MoreButton>
        </TopAuthorHeader>
      )}

      {/* Caption block — always mounted, fades in/out via opacity */}
      <CaptionAnchor $controlsVisible={showControls} $visible={showCaptions}>
        <CaptionBox>{COPY.captionPlaceholder}</CaptionBox>
      </CaptionAnchor>

      {showControls && (
        <PlayerChromeBar>
          <ProgressBarRail
            ref={scrubberRef}
            role="slider"
            aria-label={COPY.aria.seek}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, duration)}
            aria-valuenow={Math.max(0, displayTime)}
            onMouseDown={onScrubberMouseDown}
            $interactive={scrubberInteractive}
          >
            <ProgressFill style={{ width: `${progressPct}%` }}>
              <ProgressThumb />
            </ProgressFill>
          </ProgressBarRail>
          <ControlsInner>
            <PlayBtn
              ref={playBtnRef}
              type="button"
              onClick={togglePlayPause}
              aria-label={isPlaying ? COPY.aria.pause : COPY.aria.play}
              aria-pressed={isPlaying}
              disabled={!playback || !isReady}
              $hovered={playHovered}
            >
              {isPlaying ? (
                <MuxPauseIcon width="1.6em" height="1.6em" />
              ) : (
                <MuxPlayIcon width="1.6em" height="1.6em" />
              )}
            </PlayBtn>

            <Time>
              {formatPlaybackTime(displayTime)} / {formatPlaybackTime(duration)}
            </Time>

            <RightCluster>
              <ControlIconBtn aria-label={COPY.aria.settings}>
                <GearIcon width="1.6em" height="1.6em" color="#ffffff" />
              </ControlIconBtn>

              <CCButton
                type="button"
                onClick={() => onToggleVariant?.('showCaptions')}
                aria-pressed={showCaptions}
                aria-label={COPY.aria.toggleCaptions}
                $active={showCaptions}
              >
                <YouTubeCCIcon
                  width="1.8em"
                  height="1.3em"
                  strokeColor={showCaptions ? '#0f0f0f' : '#ffffff'}
                />
              </CCButton>

              <ControlIconBtn aria-label={COPY.aria.miniplayer}>
                <YouTubeMiniplayerIcon
                  width="1.7em"
                  height="1.3em"
                  strokeColor="#ffffff"
                />
              </ControlIconBtn>

              <ControlIconBtn aria-label={COPY.aria.fullscreen}>
                <FullScreenIconAlt fill="#ffffff" />
              </ControlIconBtn>

              <MuteBtn
                type="button"
                onClick={() => setIsMuted(m => !m)}
                aria-label={isMuted ? COPY.aria.unmute : COPY.aria.mute}
                aria-pressed={isMuted}
                $muted={isMuted}
              >
                {isMuted ? (
                  <SpeakerMutedIcon
                    width="1.8em"
                    height="1.8em"
                    fillColor="#ffffff"
                    strokeColor="#ffffff"
                  />
                ) : (
                  <SpeakerIcon
                    width="1.8em"
                    height="1.8em"
                    fillColor="#ffffff"
                    strokeColor="#ffffff"
                  />
                )}
              </MuteBtn>
            </RightCluster>
          </ControlsInner>
        </PlayerChromeBar>
      )}
    </OverlayFrame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 510×287 reference).
// ---------------------------------------------------------------------------

const TopAuthorHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4em;
  padding: 0.7em 1em;
  display: flex;
  align-items: center;
  gap: 0.8em;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

// Facebook's Page/profile avatar shape is rounded-square (≈ 22% radius),
// not Instagram's full circle.
const Avatar = styled.div`
  flex: 0 0 auto;
  width: 2.6em;
  height: 2.6em;
  border-radius: 22%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
`;

const BrandTextStack = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.2em;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  min-width: 0;
`;

const NameLine = styled.span`
  display: inline-block;
  min-width: 0;
  font-size: 1.3em;
  font-weight: 600;
  letter-spacing: 0.005em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VerifiedSlot = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Subtitle = styled.span`
  display: block;
  font-size: 1em;
  font-weight: 400;
  opacity: 0.9;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MoreButton = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2em;
  height: 2em;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
`;

// ----- In-video player chrome ---------------------------------------------
// Sits along the bottom edge of the video. Height 3em gives room for a
// tight progress rail along the top and a single row of controls
// underneath.
const PlayerChromeBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3em;
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.75) 0%,
    rgba(0, 0, 0, 0.35) 70%,
    rgba(0, 0, 0, 0) 100%
  );
  color: #ffffff;
`;

// Thin Facebook-blue progress rail sitting along the top edge of the player
// chrome bar. Hover-grows like YouTube's bar for a fatter hit target.
const ProgressBarRail = styled.div<{ $interactive: boolean }>`
  position: relative;
  flex: 0 0 auto;
  height: 0.3em;
  background: rgba(255, 255, 255, 0.35);
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  transition: height 100ms ease-out;

  &:hover {
    height: 0.5em;
  }
`;

const ProgressFill = styled.div`
  position: relative;
  height: 100%;
  background: ${FACEBOOK_BLUE};
`;

const ProgressThumb = styled.div`
  position: absolute;
  right: -0.55em;
  top: 50%;
  transform: translateY(-50%) scale(0);
  width: 1.1em;
  height: 1.1em;
  background: ${FACEBOOK_BLUE};
  border-radius: 50%;
  transition: transform 100ms ease-out;

  ${ProgressBarRail}:hover & {
    transform: translateY(-50%) scale(1);
  }
`;

const ControlsInner = styled.div`
  flex: 1 1 auto;
  padding: 0 0.9em;
  display: flex;
  align-items: center;
  gap: 0.8em;
  font-size: 1em;
`;

// Dim by default; brighten on cursor hover (JS-driven, see component effect
// — chrome is `pointer-events: none` so CSS `:hover` cannot fire).
const PlayBtn = styled.button<{ $hovered: boolean }>`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8em;
  height: 1.8em;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
  flex: 0 0 auto;
  opacity: ${({ $hovered }) => ($hovered ? 1 : 0.85)};
  transition: opacity 150ms ease-out;

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const Time = styled.span`
  font-size: 1.05em;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  white-space: nowrap;
  text-shadow: 0 0.05em 0.2em rgba(0, 0, 0, 0.5);
`;

const RightCluster = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.9em;
`;

const ControlIconBtn = styled.span`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: #ffffff;
  opacity: 0.9;
  cursor: pointer;
  transition: opacity 150ms ease-out;

  &:hover {
    opacity: 1;
  }
`;

// CC pill — `aria-pressed` mirrors `showCaptions`. Active paints a filled
// white pill with the glyph flipped dark.
const CCButton = styled.button<{ $active: boolean }>`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  border: none;
  border-radius: 0.3em;
  padding: 0.15em 0.25em;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
  opacity: ${({ $active }) => ($active ? 1 : 0.9)};
  transition:
    opacity 150ms ease-out,
    background 150ms ease-out;

  &:hover {
    opacity: 1;
  }
`;

const MuteBtn = styled.button<{ $muted: boolean }>`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
  opacity: ${({ $muted }) => ($muted ? 0.7 : 1)};
  transition: opacity 150ms ease-out;
`;

// ----- Captions ------------------------------------------------------------
// Anchored above the in-video player chrome (top at 3em); +1em breathing
// room → bottom: 4em. When controls are off, the chrome is gone so the
// caption drops to bottom: 0.75em near the rect bottom.
const CaptionAnchor = styled.div<{
  $controlsVisible: boolean;
  $visible: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${({ $controlsVisible }) => ($controlsVisible ? '4em' : '0.75em')};
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding: 0 0.4em;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition:
    bottom 200ms ease-out,
    opacity 200ms ease-out;
`;

// Single unified translucent box wrapping both caption lines — mirrors
// Facebook's caption rendering, which paints one full-block dark rectangle
// around the whole text rather than per-line pills. More transparent and
// smaller-typed than YouTube's per-line clone style.
const CaptionBox = styled.div`
  display: inline-block;
  color: #ffffff;
  font-size: 1.3em;
  font-weight: 500;
  letter-spacing: 0.005em;
  line-height: 1.4;
  text-align: center;
  max-width: 80%;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.4em 0.7em;
  border-radius: 0.15em;
`;
